import React from 'react'
import PropTypes from 'prop-types'
import { LazyLoadImage } from 'react-lazy-load-image-component'

import { getYoutubeThumbnailUrl } from 'domain/adPreview/format'

import youtubeLogo from 'images/youtube-logo.png'
import { LinkIcon, CirclePlusIcon, YoutubePlayIcon } from 'images'

// YouTube in-app chrome: logo header row, media, headline/description, an
// orange "Ad" tag, a blue "Learn more" button, a mock watch-next
// video-thumbnail row, and a YouTube-style bottom nav bar (Figma nodes
// 31512:16212, 31518:16515 — see googleDisplayVariations.js).
//
// When `ad.companionBanner` is present, it renders next to the CTA rather than
// in the media area (a companion banner is a static image shown alongside a
// YouTube video ad).
const YoutubeChromeCard = ({ ad }) => {
  const { headline, description, media, callToAction, businessName, logo } = ad

  return (
    <div className="google-display-card google-display-card--youtube">
      <div className="google-display-card__yt-header">
        <img className="google-display-card__yt-logo" src={youtubeLogo} alt="YouTube" />

        <div className="google-display-card__yt-header-right">
          {Array.from({ length: 3 }).map((_, index) => (
            <div key={index} className="google-display-card__yt-header-right-item" />
          ))}
        </div>
      </div>

      <div className="google-display-card__yt-media-wrap">
        {media?.youtube_video_id ? (
          // A real youtube_video_id item never has a `url` (see
          // mapYoutubeVideoForPayload) — render YouTube's own public
          // thumbnail instead of a sourceless <video> tag, same approach
          // YouTubeUrlInput.jsx's own preview card takes.
          <LazyLoadImage
            className="google-display-card__media"
            src={getYoutubeThumbnailUrl(media.youtube_video_id)}
            alt={media?.filename}
            visibleByDefault
          />
        ) : (
          // visibleByDefault: see BareGradientCard.jsx — avoids the lazy-load
          // placeholder's raw-pixel inline height inflating a horizontally-
          // scrolling row for any card not yet scrolled into view.
          <LazyLoadImage
            className="google-display-card__media"
            src={media?.url}
            alt={media?.filename}
            width={media?.width}
            height={media?.height}
            visibleByDefault
          />
        )}

        <div className="google-display-card__yt-visit-site">
          <p>{callToAction}</p>

          <LinkIcon width={12} height={12} />
        </div>

        <span className="google-display-card__ad-tag">Ad</span>
      </div>

      <div className="google-display-card__yt-body">
        {logo?.url ? (
            <img className="google-display-card__yt-logo" src={logo.url} alt={businessName} />
          ) : (
            <span className="google-display-card__yt-logo-fallback" />
        )} 

        <div>
          <div className="google-display-card__headline">{headline}</div>

          <div className="google-display-card__description">{description}</div>

          <div className="google-display-card__sponsored-container">
            <span className="google-display-card__sponsored">Sponsored</span>

            <span>•</span>
            
            <span className="google-display-card__business-name">{businessName}</span>
          </div>
        </div>
      </div>

      <div className="google-display-card__yt-watch-next">
        <span className="google-display-card__yt-thumb" />

        <YoutubePlayIcon width={68} color="white" className="google-display-card__yt-play-icon"/>
      </div>

      <div className="google-display-card__shorts-nav" aria-hidden="true">
        <span className="google-display-card__shorts-nav-item" />
        <span className="google-display-card__shorts-nav-item" />
        <span className="google-display-card__shorts-nav-item google-display-card__shorts-nav-item--create">
          <CirclePlusIcon width={28} height={28} />
        </span>
        <span className="google-display-card__shorts-nav-item" />
        <span className="google-display-card__shorts-nav-item" />
      </div>
    </div>
  )
}

YoutubeChromeCard.propTypes = {
  ad: PropTypes.shape({
    headline: PropTypes.string,
    description: PropTypes.string,
    callToAction: PropTypes.string,
    media: PropTypes.object,
    companionBanner: PropTypes.object,
    businessName: PropTypes.string,
  }).isRequired,
}

export default YoutubeChromeCard
